/**
 * Contextual Suspense fallback for authenticated app routes.
 *
 * Eager, skeleton-only imports — never pulls page modules. Replaces the old
 * `fallback={null}` on primary sidebar paths so the main area shows a
 * structural shimmer while the lazy route chunk downloads.
 */
import { PageLoader } from './PageLoader';
import {
  ConversationListSkeleton,
  ChatPanelIdleSkeleton,
} from '../pages/inbox/inboxPanels/InboxSkeletons';
import {
  GroupListSkeleton,
  MessageThreadSkeleton,
  ThreadHeaderSkeleton,
} from '../components/buzztalk/BuzzTalkSkeletons';
import { StudioViewSkeleton } from '../pages/email-marketing/StudioSkeletons';
import { CalendarTabSkeleton } from '../pages/marketing/SocialAutomator/calendarParts/CalendarSkeletons';
import { ProfileTabSkeleton } from '../pages/settings/AccountSkeletons';
import { BusinessHubSkeleton } from '../pages/business/BusinessHubSkeleton';
import { Skeleton } from '../components/ui/Skeleton';

function InboxRouteSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-[50vh] flex-col bg-gray-50" role="status" aria-busy="true">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="mt-2 h-3 w-56 max-w-full" />
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="hidden w-80 shrink-0 border-r border-gray-200 bg-white sm:block">
          <ConversationListSkeleton />
        </div>
        <ChatPanelIdleSkeleton />
      </div>
    </div>
  );
}

function ChatRouteSkeleton(): JSX.Element {
  return (
    <div className="flex h-full min-h-[50vh] flex-col bg-gray-50" role="status" aria-busy="true">
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-7 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24 rounded-full" />
            <Skeleton className="h-8 w-28 rounded-full" />
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="hidden w-72 shrink-0 border-r border-gray-200 bg-white lg:block">
          <GroupListSkeleton />
        </div>
        <div className="flex min-w-0 flex-1 flex-col bg-white px-4">
          <ThreadHeaderSkeleton />
          <MessageThreadSkeleton />
        </div>
      </div>
    </div>
  );
}

function AccountRouteSkeleton(): JSX.Element {
  return (
    <div className="account-page w-full p-4 sm:p-6" role="status" aria-busy="true">
      <Skeleton className="mb-2 h-7 w-48" />
      <Skeleton className="mb-6 h-3 w-72 max-w-full" />
      <div className="mb-8 flex gap-4 overflow-hidden border-b border-gray-200 pb-px">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 shrink-0 rounded-none" />
        ))}
      </div>
      <ProfileTabSkeleton />
    </div>
  );
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function RouteContentFallback({ pathname }: { pathname: string }): JSX.Element {
  if (matchesPrefix(pathname, '/inbox')) {
    return <InboxRouteSkeleton />;
  }
  if (matchesPrefix(pathname, '/chat')) {
    return <ChatRouteSkeleton />;
  }
  if (
    matchesPrefix(pathname, '/studio') ||
    matchesPrefix(pathname, '/automations') ||
    matchesPrefix(pathname, '/marketing/email') ||
    matchesPrefix(pathname, '/popups')
  ) {
    return <StudioViewSkeleton />;
  }
  if (matchesPrefix(pathname, '/calendar') || matchesPrefix(pathname, '/marketing/social')) {
    return <CalendarTabSkeleton />;
  }
  if (
    matchesPrefix(pathname, '/account') ||
    matchesPrefix(pathname, '/brand-dna') ||
    matchesPrefix(pathname, '/settings')
  ) {
    return <AccountRouteSkeleton />;
  }
  if (matchesPrefix(pathname, '/business')) {
    return <BusinessHubSkeleton />;
  }
  return <PageLoader />;
}
